import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import {memoizeWith, T} from 'ramda';
import React, {memo, useEffect, useMemo, useState} from 'react';
import {Query} from '@apollo/react-components';
import {withRouter} from 'react-router';
import makeTimeDateCell from '../atoms/makeTimeDateCell';
import {withErrorBoundary} from '../molecules/ErrorBoundary';
import CheckboxFilter, {
  compileCheckboxFilter,
} from '../molecules/ListFilter/CheckboxFilter';
import Loading from '../molecules/Loading';
import {alarmColor} from '../molecules/SideBarAlarm';
import ErrorPage from '../pages/ErrorPage';
import {colors} from '../styles';
import {isBypassAlarm, isCategory} from '../utils/alarms';
import {useToggleState} from '../utils/hooks';
import AmpTable, {defaultTrProps} from './Table';

const columns = [
  {
    Header: 'ALARM',
    accessor: 'code',
    width: 100,
    getProps: (_, rowInfo) => {
      const indicatorColor = alarmColor.get(rowInfo.original.category);
      return {
        style: {
          borderLeft: `8px solid ${indicatorColor}`,
          borderTopLeftRadius: '4px',
          borderBottomLeftRadius: '4px',
        },
      };
    },
  },
  {
    Header: 'TIME & DATE (UTC)',
    accessor: 'start',
    Cell: makeTimeDateCell('start'),
    width: 180,
  },
  {
    Header: 'ALARM TYPE',
    accessor: 'text',
    width: 400,
  },
];

const defaultFilters = [
  {
    label: 'Yellow',
    fn: isCategory('YELLOW'),
    color: colors.alarm.yellow,
    checked: true,
  },
  {
    label: 'Orange',
    fn: isCategory('ORANGE'),
    color: colors.alarm.orange,
    checked: true,
  },
  {label: 'Red', fn: isCategory('RED'), color: colors.alarm.red, checked: true},
  {
    label: 'Black',
    fn: isCategory('BLACK'),
    color: colors.alarm.black,
    checked: true,
  },
  {
    label: 'Bypass',
    fn: isBypassAlarm,
    color: colors.alarm.yellow,
    checked: true,
  },
];

export const GET_ALARM_HISTORY = gql`
  query System($id: ID!) {
    system(id: $id) {
      id
      alarmHistory {
        text
        code
        category
        status
        start
        order
      }
    }
  }
`;

const compileFilters = memoizeWith(
  filters => filters.map(x => x.checked).join(','),
  compileCheckboxFilter,
);

const AlarmsListFilters = ({onFilterChange}) => {
  const [alarmFilters, toggleFilter] = useToggleState(defaultFilters);

  const filterFn = compileFilters(alarmFilters);
  useEffect(() => {
    onFilterChange(filterFn);
  }, [filterFn, onFilterChange]);

  return (
    <CheckboxFilter
      filterName="alarm"
      label="Filter on alarm:"
      onChange={toggleFilter}
      filters={alarmFilters}
    />
  );
};

AlarmsListFilters.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
};

const AlarmsListContent = memo(({data}) => {
  const [filterFn, setFilterFn] = useState(() => T);

  const filteredData = useMemo(() => data.filter(filterFn), [data, filterFn]);

  return (
    <>
      <AlarmsListFilters onFilterChange={fn => setFilterFn(() => fn)} />
      <AmpTable
        sortable={false}
        data={filteredData}
        style={{flex: 1, height: '100%'}}
        columns={columns}
        getTheadThProps={() => ({
          sortable: false,
        })}
        getTrProps={(_, row) => ({
          ...defaultTrProps,
          clickable: false,
          style: {
            pointer: 'none',
            background: row.original.status === 0 && 'rgba(10, 10, 255, 0.05)',
            borderRadius: row.original.status === 0 && '4px',
          },
        })}
        getTdProps={(_, row) => ({
          style: {
            pointer: 'none',
            background: row.original.status === 0 && 'rgba(17, 35, 105, 0.05)',
            textTransform: 'none',
          },
        })}
      />
    </>
  );
});

AlarmsListContent.propTypes = {
  data: PropTypes.array.isRequired,
};

const AlarmsList = ({match}) => {
  const {
    params: {id},
  } = match;

  return (
    <Query
      query={GET_ALARM_HISTORY}
      variables={{id}}
      pollInterval={10000}
      fetchPolicy="network-only"
    >
      {({loading, error, data}) => {
        if (error) {
          return <ErrorPage error={error} />;
        }
        if (loading) {
          return <Loading loading={loading} message="Fetching alarm data..." />;
        }
        const alarms = data ? data.system.alarmHistory : [];
        return <AlarmsListContent data={alarms} />;
      }}
    </Query>
  );
};

AlarmsList.propTypes = {
  match: PropTypes.object,
};

export default withErrorBoundary(withRouter(AlarmsList));
